import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Sparkles, Heart, ArrowRight, CheckCircle } from 'lucide-react';

export default function SanctuaryWelcome() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('checking'); // checking | active | failed
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/membership/checkout-status/${sessionId}`);
        if (data.payment_status === 'paid') {
          // refresh user context so the header sanctuary link shows
          try { await checkAuth(); } catch (_) {}
          setStatus('active');
          return;
        }
      } catch (_) {}
      if (attempts >= maxAttempts) {
        setStatus('failed');
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, checkAuth]);

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        {status === 'checking' && (
          <div data-testid="sanctuary-welcome-checking">
            <div className="animate-spin rounded-full h-14 w-14 border-b-2 mx-auto mb-6" style={{ borderColor: '#E57A44' }} />
            <h1 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Setting up your Sanctuary…</h1>
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Confirming your payment with Stripe. This takes a few seconds.</p>
          </div>
        )}

        {status === 'active' && (
          <div data-testid="sanctuary-welcome-active">
            <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #78A18E 0%, #E8C265 100%)' }}>
              <CheckCircle className="w-10 h-10 text-white" strokeWidth={2} />
            </div>
            <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Welcome to the Sanctuary</h1>
            <p className="text-lg mb-8" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.7 }}>
              You're in. Post a prayer intention, request an article, or just spend a little time with the community.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={() => navigate('/sanctuary/dashboard')}
                className="px-6 py-3 rounded-full flex items-center gap-2 hover:-translate-y-0.5 transition"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 700 }}
                data-testid="sanctuary-welcome-goto-dashboard"
              >
                <Sparkles className="w-4 h-4" /> Enter The Sanctuary <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/sanctuary/prayer-wall" className="px-6 py-3 rounded-full flex items-center gap-2 hover:-translate-y-0.5 transition"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FFFFFF', color: '#2C2724', fontWeight: 600, border: '1.5px solid #E6E2D8' }}>
                <Heart className="w-4 h-4" /> Post a prayer
              </Link>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div data-testid="sanctuary-welcome-failed">
            <h1 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Still finishing up…</h1>
            <p className="mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Your payment is being processed. This can take a minute — refresh in a moment, or head to your dashboard to check.</p>
            <button
              onClick={() => navigate('/sanctuary/dashboard')}
              className="px-6 py-3 rounded-full inline-flex items-center gap-2 hover:-translate-y-0.5 transition"
              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 700 }}
            >
              Go to Dashboard <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
